import React, { useCallback, useEffect, useRef, useState } from 'react';

// PROD-8664 (deploy awareness). Agents keep CMS tabs open for entire shifts —
// sometimes across deploys — so they can be running a bundle that is hours or
// days stale. That is one of the conditions a "ghost" Twilio registration
// shows up under, and more generally it means bug fixes don't reach the people
// who reported them until they happen to reload.
//
// Detection needs no build-pipeline change. Vite content-hashes the entry
// bundle into index.html (`/assets/index-<hash>.js`), so the set of same-origin
// script srcs in a freshly fetched index.html IS the deploy identity. We snap a
// baseline from our own document at startup and compare periodically.
//
// Deliberately banner-only: no auto-reload. A CMS tab routinely holds unsaved
// work (composer drafts, half-filled forms), and reloading under an agent to
// save them one click is a bad trade. The reload is one click away and blocked
// while a call is live.
const POLL_INTERVAL_MS = 15 * 60 * 1000;

// Only our own bundle files can testify about a deploy. index.html appends a
// cross-origin lottie-player tag at parse time, and there are font/analytics
// tags besides; those change on someone else's schedule and would produce
// false positives.
const fingerprintFromSrcList = (srcs) => {
  if (typeof window === 'undefined') return '';
  const { origin } = window.location;
  return (
    srcs
      .map((src) => (src || '').trim())
      .filter(Boolean)
      .filter((src) => src.startsWith('/') || src.startsWith(origin))
      .map((src) => src.replace(origin, ''))
      // Vite hashes the filename, not the query string.
      .map((src) => src.split('?')[0])
      .sort()
      .join('|')
  );
};

const fingerprintFromHtml = (html) => {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // getAttribute, not .src: a DOMParser document has no base URL, so .src
    // on a root-relative path resolves inconsistently across browsers.
    return fingerprintFromSrcList(
      Array.from(doc.querySelectorAll('script[src]')).map((s) => s.getAttribute('src'))
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('new_version.parse_failed', e);
    return '';
  }
};

// Snapshotted at module load, before React starts lazy-loading route chunks,
// so the baseline can only ever contain the entry script index.html shipped us.
const currentFingerprint =
  typeof document === 'undefined'
    ? ''
    : fingerprintFromSrcList(
        Array.from(document.querySelectorAll('script[src]')).map((s) => s.getAttribute('src'))
      );

const callIsActive = () =>
  typeof window !== 'undefined' &&
  (!!window.__twilioIncomingActive || !!window.__twilioOutboundActive);

export default function NewVersionBanner() {
  const [stale, setStale] = useState(false);
  const [blockedByCall, setBlockedByCall] = useState(false);
  // Once we've decided the bundle is stale, stop polling — the answer cannot
  // change back, and the banner is already up.
  const staleRef = useRef(false);

  const check = useCallback(async () => {
    if (staleRef.current) return;
    try {
      const resp = await fetch(`/index.html?_v=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'same-origin'
      });
      if (!resp.ok) return;
      const next = fingerprintFromHtml(await resp.text());
      // An empty fingerprint on either side means we couldn't read the deploy
      // identity (odd nginx response, parse failure). Staying quiet is right:
      // a spurious "new version" banner would train agents to ignore it.
      if (!next || !currentFingerprint) return;
      if (next === currentFingerprint) return;

      // eslint-disable-next-line no-console
      console.info('new_version.detected', { from: currentFingerprint, to: next });
      staleRef.current = true;
      setStale(true);
    } catch (e) {
      // Offline or a blip — try again next tick, silently.
    }
  }, []);

  useEffect(() => {
    const id = setInterval(check, POLL_INTERVAL_MS);
    // Also check when the tab comes back to the foreground: the likeliest
    // moment for a long-idle tab to be many deploys behind.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  // Keep the reload button's enabled state honest while the banner is up.
  useEffect(() => {
    if (!stale) return undefined;
    const sync = () => setBlockedByCall(callIsActive());
    sync();
    window.addEventListener('twilio-incoming-call-active', sync);
    window.addEventListener('twilio-outgoing-call-active', sync);
    const id = setInterval(sync, 2000);
    return () => {
      window.removeEventListener('twilio-incoming-call-active', sync);
      window.removeEventListener('twilio-outgoing-call-active', sync);
      clearInterval(id);
    };
  }, [stale]);

  if (!stale) return null;

  const onReload = () => {
    // Re-check at the instant of the click, not just on the polled state.
    if (callIsActive()) {
      setBlockedByCall(true);
      return;
    }
    window.location.reload();
  };

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        // Bottom-left: the incoming/outbound call pills own the top-right.
        bottom: '20px',
        left: '20px',
        zIndex: 9998,
        background: 'var(--surface-card)',
        border: '1px solid var(--border-default)',
        borderRadius: '10px',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        maxWidth: '360px'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', lineHeight: 1.3 }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
          A new version is available
        </span>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
          {blockedByCall ? 'You can reload once your current call ends.' : 'Reload to update.'}
        </span>
      </div>
      <button
        type="button"
        onClick={onReload}
        disabled={blockedByCall}
        style={{
          marginLeft: 'auto',
          border: 0,
          borderRadius: '6px',
          padding: '6px 12px',
          fontSize: '12px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          color: '#fff',
          background: 'var(--brand-primary)',
          opacity: blockedByCall ? 0.5 : 1,
          cursor: blockedByCall ? 'not-allowed' : 'pointer'
        }}
      >
        Reload
      </button>
    </div>
  );
}
